import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { theme, Card, Typography, Descriptions, Tag, Button, Space } from 'antd';
import { CircleCheck, CircleX, ShieldCheck, Headphones } from 'lucide-react';
import Icon from '../../components/Icon';
import api from '../../services/apiService';
import './VerifyCertificate.css';
import MainLayout from '../../components/MainLayout';
import { alpha } from '../../utils/color';
import { useThemeMode } from '../../theme/ThemeProvider';
import { getAirsmartLogo } from '../../theme/brandingConfig';

const { Title, Text } = Typography;

const VerifyCertificate = () => {
  const { certificateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const { token } = theme.useToken();
  const { mode } = useThemeMode();
  const verifyLogo = getAirsmartLogo(mode);

  useEffect(() => {
    const verify = async () => {
      try {
        const data = await api.get(`/certificate/verify/${certificateId}`);
        setResult(data);
      } catch (err) {
        console.error('Verification error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (certificateId) {
      verify();
    }
  }, [certificateId]);

  if (loading && !result) {
    return null; // Don't show loading spinner as requested
  }

  const isValid = result?.valid;
  const cert = result?.certificate;
  const isExpired = cert?.expireOn && new Date(cert.expireOn) < new Date();

  return (
    <MainLayout hideHeader={true}>
      <div className="verify-content">
        <div className="verify-card-wrapper">
          {isValid ? (
            <Card
              className="cert-card"
              style={{
                background: token.colorBgContainer,
                borderColor: token.colorBorderSecondary,
                color: token.colorText,
              }}
            >
              <div className="cert-header-section">
                <img src={verifyLogo} alt="AirSmart" className="verify-logo" />
                {isExpired ? (
                  <Icon as={CircleX} strokeWidth={2.5} className="status-icon warning" style={{ color: token.colorWarning }} />
                ) : (
                  <Icon as={CircleCheck} strokeWidth={2.5} className="status-icon success" style={{ color: token.colorSuccess }} />
                )}
                <Title level={2} className={`status-title ${isExpired ? 'warning' : ''}`} style={isExpired ? { color: token.colorWarning } : {}}>
                  {isExpired ? 'CERTIFICATE EXPIRED' : 'VERIFIED AUTHENTIC'}
                </Title>
                <Text className="cert-intro">
                  {isExpired 
                    ? 'This digital record indicates that the professional accreditation for the individual below has expired and is no longer active.'
                    : 'This digital record confirms that the individual below has successfully completed professional accreditation within the AirSmart Certified Network.'
                  }
                </Text>
              </div>
              
              <Descriptions 
                bordered 
                column={1} 
                className="cert-descriptions"
                labelStyle={{ 
                  fontWeight: 400, 
                  color: token.colorTextDescription, 
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontSize: '11px',
                  fontFamily: 'var(--font-sf-pro)',
                  background: alpha(token.colorFillQuaternary, 0.5)
                }}
                contentStyle={{ 
                  fontWeight: 400, 
                  color: token.colorText,
                  fontFamily: 'var(--font-untitled-sans)',
                  fontSize: '15px'
                }}
              >
                <Descriptions.Item label="ACCREDITED PROFESSIONAL">
                  {cert.userName || 'Unknown User'}
                </Descriptions.Item>
                <Descriptions.Item label="ACCREDITATION TYPE">
                  <Tag 
                    style={{ 
                      fontWeight: 600, 
                      borderRadius: 2,
                      border: 'none',
                      background: alpha(token.gold, 0.1),
                      color: token.gold
                    }}
                  >
                    {cert.courseName || 'Certified AirSmart Specialist'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="COMPLETION DATE">
                  {new Date(cert.completeOn).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </Descriptions.Item>
                {cert.expireOn && (
                  <Descriptions.Item label="EXPIRE DATE">
                    <Space>
                      {new Date(cert.expireOn).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                      {isExpired && (
                        <Tag color="error" style={{ borderRadius: 2, fontSize: '10px', fontWeight: 700 }}>
                          EXPIRED
                        </Tag>
                      )}
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="CERTIFICATE ID">
                  <Text code style={{ fontSize: '13px' }}>{certificateId.toUpperCase()}</Text>
                </Descriptions.Item>
              </Descriptions>

              <div className="cert-footer-note">
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <Icon as={ShieldCheck} size={24} style={{ color: token.gold }} />
                  <Text style={{ 
                    fontSize: '10px', 
                    letterSpacing: '3px', 
                    fontWeight: 400, 
                    textTransform: 'uppercase',
                    color: token.colorTextDescription,
                    fontFamily: 'var(--font-sf-pro)'
                  }}>
                    OFFICIAL ACCREDITATION RECORD
                  </Text>
                </Space>
              </div>
            </Card>
          ) : (
            <Card
              className="cert-card error"
              style={{
                background: token.colorBgContainer,
                borderColor: token.colorBorderSecondary,
                color: token.colorText,
              }}
            >
              <div className="cert-header-section">
                <img src={verifyLogo} alt="AirSmart" className="verify-logo" />
                <Icon as={CircleX} strokeWidth={2.5} className="status-icon error" />
                <Title level={2} className="status-title error">INVALID CERTIFICATE</Title>
                <Text className="cert-intro">
                  We could not find a matching record for this certificate ID in our secure verification system.
                </Text>
              </div>

              <div className="error-hint-premium">
                <Text style={{ color: token.colorError }}>
                  Please ensure the ID in the URL is correct. If you believe this is an error, please contact AirSmart support.
                </Text>
              </div>

              <div className="verify-actions">
                <Button
                  icon={<Icon as={Headphones} />}
                  onClick={() => window.open('https://airsmart.com/contact', '_blank', 'noopener,noreferrer')}
                  className="verify-btn-premium secondary"
                >
                  CONTACT SUPPORT
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default VerifyCertificate;
